// Font embedding for docx export
// Uint8Array is used in place of Buffer since both are compatible at runtime

interface FontFace {
  name: string; // Font name referenced in TextRun
  path: string; // Public path to the TTF file
}

/** Font face data ready for docx Document consumption */
interface DocxFontFace {
  name: string;
  data: Buffer;
}

const createFontFaces = (name: string, paths: string[]): readonly FontFace[] =>
  paths.map((path) => ({ name, path }));

/** Fetches font TTF files in parallel and returns docx font options. Failed fonts are skipped */
export const loadFontFaces = async (
  faces: readonly FontFace[],
): Promise<DocxFontFace[]> => {
  const results = await Promise.allSettled(
    faces.map(async ({ name, path }) => {
      const res = await fetch(path);
      // docx types expect Buffer but Uint8Array works at runtime
      const data = new Uint8Array(await res.arrayBuffer()) as unknown as Buffer;

      return { name, data };
    }),
  );

  return results
    .filter(
      (r): r is PromiseFulfilledResult<DocxFontFace> =>
        r.status === 'fulfilled',
    )
    .map((r) => r.value);
};

/** Poppins font faces mirroring the PDF registration */
export const POPPINS_FONT_FACES = createFontFaces('Poppins', [
  '/fonts/Poppins-Regular.ttf',
  '/fonts/Poppins-Medium.ttf',
  '/fonts/Poppins-SemiBold.ttf',
  '/fonts/Poppins-Italic.ttf',
  '/fonts/Poppins-MediumItalic.ttf',
  '/fonts/Poppins-SemiBoldItalic.ttf',
]);

/** Proxima Nova font faces mirroring the PDF registration */
export const PROXIMA_NOVA_FONT_FACES = createFontFaces('Proxima Nova', [
  '/fonts/ProximaNova-Regular.ttf',
  '/fonts/ProximaNova-Light.ttf',
  '/fonts/ProximaNova-Semibold.ttf',
  '/fonts/ProximaNova-Bold.ttf',
]);
